import { useMemo, useState } from 'react';
import { useBookSession, useTutorAvailability, useTutors } from '../../api/sessions';
import { Avatar } from '../../components/Avatar';
import { Badge, PresenceDot } from '../../components/Badge';
import { SearchInput } from '../../components/FilterBar';
import { EmptyState, ErrorState } from '../../components/EmptyState';
import { Spinner } from '../../components/Spinner';
import { useToast } from '../../components/Toast';
import { useDebounced } from '../../hooks/useDebounced';
import { formatMoney, formatSlotRange } from '../../lib/format';
import { ApiError } from '../../lib/api';
import type { TutorSort } from '../../types/api';

/** Booking on a student's behalf — the "I'll sort that out for you" path. */
export function BookTab() {
  const { notify } = useToast();
  const [search, setSearch] = useState('');
  const [subject, setSubject] = useState('');
  const [sort, setSort] = useState<TutorSort>('rating');
  const [selected, setSelected] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [bookingSubject, setBookingSubject] = useState('');

  const debounced = useDebounced(search);
  const query = useMemo(
    () => ({
      q: debounced.trim() || undefined,
      subject: subject.trim() || undefined,
      sort,
      limit: 20,
    }),
    [debounced, subject, sort],
  );

  const tutors = useTutors(query);
  const availability = useTutorAvailability(selected ?? undefined);
  const book = useBookSession();

  const selectedTutor = tutors.data?.data.find((t) => t.id === selected);

  const bookSlot = async (slotId: string) => {
    try {
      const session = await book.mutateAsync({
        slotId,
        subject: bookingSubject.trim() || undefined,
        note: note.trim() || undefined,
      });
      notify(`Booked with ${session.tutor.fullName}`, 'success');
      setNote('');
      setBookingSubject('');
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'Could not book this slot.', 'error');
    }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_22rem]">
      <div className="panel overflow-hidden">
        <div className="flex flex-wrap items-center gap-2 border-b border-rule px-3 py-2.5">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Search tutors by name"
          />
          <input
            className="input h-[2.125rem] w-auto"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Subject, e.g. algebra"
          />
          <select
            className="select ml-auto h-[2.125rem] w-auto"
            value={sort}
            aria-label="Sort tutors"
            onChange={(e) => setSort(e.target.value as TutorSort)}
          >
            <option value="rating">Best rated</option>
            <option value="priceAsc">Cheapest first</option>
            <option value="priceDesc">Most expensive first</option>
            <option value="newest">Newest</option>
          </select>
        </div>

        {tutors.isLoading ? (
          <div className="flex h-32 items-center justify-center text-ink-3">
            <Spinner />
          </div>
        ) : tutors.isError ? (
          <ErrorState
            message={tutors.error instanceof ApiError ? tutors.error.message : undefined}
            onRetry={() => void tutors.refetch()}
          />
        ) : tutors.data && tutors.data.data.length > 0 ? (
          <ul className="divide-y divide-rule">
            {tutors.data.data.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  onClick={() => setSelected(t.id === selected ? null : t.id)}
                  className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-surface-hover ${
                    selected === t.id ? 'bg-accent-wash' : ''
                  }`}
                >
                  <Avatar name={t.fullName} src={t.avatarUrl} />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="font-medium text-ink">{t.fullName}</span>
                      {t.isVerified && <Badge tone="good">Verified</Badge>}
                      <PresenceDot online={t.isOnline} />
                    </div>
                    {t.headline && (
                      <div className="mt-0.5 truncate text-xs text-ink-2">{t.headline}</div>
                    )}
                    <div className="mt-1 flex flex-wrap gap-1">
                      {t.subjects.slice(0, 4).map((s) => (
                        <Badge key={s}>{s}</Badge>
                      ))}
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="text-[0.8125rem] font-medium text-ink">
                      {formatMoney(t.hourlyRateCents, t.currency)}
                    </div>
                    <div className="text-[0.6875rem] text-ink-3">per hour</div>
                    {t.ratingCount > 0 && (
                      <div className="mt-1 text-[0.6875rem] text-ink-3">
                        {t.ratingAvg.toFixed(1)} ({t.ratingCount})
                      </div>
                    )}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState
            title="No tutors match"
            description="Try a different subject or clear the search."
          />
        )}
      </div>

      <div className="panel p-4">
        <h3 className="text-sm font-semibold text-ink">
          {selectedTutor ? `Open times — ${selectedTutor.fullName}` : 'Open times'}
        </h3>

        {!selected ? (
          <p className="mt-3 text-xs text-ink-3">
            Pick a tutor on the left to see when they are free.
          </p>
        ) : (
          <>
            <div className="mt-4 space-y-3">
              <label className="block">
                <span className="label">Subject (optional)</span>
                <input
                  className="input"
                  value={bookingSubject}
                  onChange={(e) => setBookingSubject(e.target.value)}
                  placeholder="algebra"
                />
              </label>
              <label className="block">
                <span className="label">Note for the tutor (optional)</span>
                <textarea
                  className="textarea"
                  rows={2}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Needs help with quadratic equations"
                />
              </label>
            </div>

            <div className="mt-4 border-t border-rule pt-3">
              {availability.isLoading ? (
                <Spinner />
              ) : availability.data && availability.data.length > 0 ? (
                <ul className="space-y-1.5">
                  {availability.data.map((slot) => (
                    <li key={slot.id} className="flex items-center gap-2">
                      <span className="flex-1 text-xs text-ink-2">
                        {formatSlotRange(slot.startTime, slot.endTime)}
                      </span>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        disabled={book.isPending}
                        onClick={() => void bookSlot(slot.id)}
                      >
                        Book
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-ink-3">
                  This tutor has no open times published.
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
